import { CurrencyType } from 'utils/formatCurrency/formatCurrency.model';
import { PaymentType } from '../../pages/Dashboard/pages/Payment/Payment.model';

export enum NotificationTextType {
  INSTALLMENT = 'INSTALLMENT',
  DEFAULT = 'DEFAULT'
}

export interface MoneyDTO {
  amount: number;
  currency: CurrencyType;
}
// TODO, explain to team why this is needed i.e. separate omit instead blob
type DistributiveOmit<T, K extends PropertyKey> = T extends T
  ? Omit<T, K>
  : never;

export type VariantType = 'error' | 'success' | 'warning' | 'info';

// non reusable outside
type BaseNotification = {
  createdAt: Date;
  variant: VariantType;
};

// to bypass not foreseen nullish value
type NonNullableContext<T> = T extends { context: infer C }
  ? Omit<T, 'context'> & { context: NonNullable<C> }
  : T;

export type Notification = NonNullableContext<
  DistributiveOmit<NotificationDTO, 'createdAt'>
> &
  BaseNotification;

export type NotificationsState = { items: Notification[] };

export const SEVERITY_TO_VARIANT_CONFIG: Record<
  NotificationSeverity,
  VariantType
> = {
  ALERT: 'error',
  WARNING: 'warning',
  // or success, not sure yet
  INFORMATION: 'success',
  // success does not exist in the API, but it is used in the UI
  SUCCESS: 'success'
};

export type NotificationDefinition =
  | 'EOD_IN_PROGRESS'
  | 'CLIENT_SYNCHRONIZATION_OUTDATED'
  | 'REPAYMENT'
  | 'ONE_TIME_PAYMENT_DUE_SOON'
  | 'INSTALLMENT_PAYMENT_DUE_SOON'
  | 'PREREQUISITE_PAYMENT_DUE_SOON'
  | 'ONE_TIME_PAYMENT_DUE_TODAY'
  | 'INSTALLMENT_PAYMENT_DUE_TODAY'
  | 'PREREQUISITE_PAYMENT_DUE_TODAY'
  | 'ONE_TIME_PAYMENT_OVERDUE'
  | 'INSTALLMENT_PAYMENT_OVERDUE'
  | 'INSTALLMENTS_PAYMENT_OVERDUE'
  | 'PREREQUISITE_PAYMENT_OVERDUE';

// @ts-ignore
export const NOTIFICATION_DEFINTION_TO_PAYMENT_TYPE: {
  [K in NotificationDefinition]: PaymentType;
} = {
  INSTALLMENTS_PAYMENT_OVERDUE: PaymentType.OVERDUE_INSTALLMENTS,
  REPAYMENT: PaymentType.CUSTOM_AMOUNT,
  ONE_TIME_PAYMENT_DUE_SOON: PaymentType.CONDITIONAL_PAYMENT,
  INSTALLMENT_PAYMENT_DUE_SOON: PaymentType.NEXT_INSTALLMENT,
  PREREQUISITE_PAYMENT_DUE_SOON: PaymentType.CONDITIONAL_PAYMENT,
  ONE_TIME_PAYMENT_DUE_TODAY: PaymentType.CONDITIONAL_PAYMENT,
  INSTALLMENT_PAYMENT_DUE_TODAY: PaymentType.NEXT_INSTALLMENT,
  PREREQUISITE_PAYMENT_DUE_TODAY: PaymentType.CONDITIONAL_PAYMENT,
  ONE_TIME_PAYMENT_OVERDUE: PaymentType.CONDITIONAL_PAYMENT,
  INSTALLMENT_PAYMENT_OVERDUE: PaymentType.OVERDUE_INSTALLMENT,
  PREREQUISITE_PAYMENT_OVERDUE: PaymentType.OVERDUE_INSTALLMENT
};

export type NotificationSeverity =
  | 'ALERT'
  | 'WARNING'
  | 'INFORMATION'
  // success does not exist in the API, but it is used in the UI
  | 'SUCCESS';

export type NotificationType =
  | 'DEFAULT'
  | 'SYNCHRONIZATION'
  | 'PAYMENT'
  | 'REPAYMENT';

export interface PaymentNotificationContext {
  contractId: number;
  daysUntilDueDate: number;
  paymentAmount: MoneyDTO;
  currentDebtAmount: MoneyDTO;
  sourceContractId: string;
}
export type PaymentNotification = Extract<Notification, { type: 'PAYMENT' }>;

export type RepaymentNotification = Extract<
  Notification,
  { type: 'REPAYMENT' }
>;

export type PaymentNotificationDTO = {
  name: NotificationDefinition;
  severity: NotificationSeverity;
  type: Extract<NotificationType, 'PAYMENT'>;
  createdAt: string;
  displayed: boolean;
  messageKey: string;
  messageParams?: Record<string, Object>;
  context: PaymentNotificationContext;
};

export interface RepaymentNotificationContext {
  contractId: number;
  paymentAmount: MoneyDTO;
  sourceContractId: string;
  postingDate: string;
}

export type RepaymentNotificationDTO = {
  name: NotificationDefinition;
  severity: NotificationSeverity;
  type: Extract<NotificationType, 'REPAYMENT'>;
  createdAt: string;
  displayed: boolean;
  messageKey: string;
  messageParams?: Record<string, Object>;
  context: RepaymentNotificationContext;
};

export interface SynchronizationNotificationContext {
  synchronizationTime: string;
}

export type SynchronizationNotificationDTO = {
  name: NotificationDefinition;
  severity: NotificationSeverity;
  type: Extract<NotificationType, 'SYNCHRONIZATION'>;
  createdAt: string;
  displayed: boolean;
  messageKey: string;
  messageParams?: Record<string, Object>;
  context: SynchronizationNotificationContext;
};

export type DefaultNotificationDTO = {
  name: NotificationDefinition;
  severity: NotificationSeverity;
  type: Extract<NotificationType, 'DEFAULT'>;
  createdAt: string;
  displayed: boolean;
  messageKey: string;
  messageParams?: Record<string, Object>;
  context: Record<string, never> | null;
};

export type NotificationDTO =
  | PaymentNotificationDTO
  | RepaymentNotificationDTO
  | SynchronizationNotificationDTO
  | DefaultNotificationDTO;
